import { Typography } from "antd"
import React from "react"

import type { FormContext } from "./form-field-requirements.types.ts"

const { Text } = Typography

interface ContextStepperProps {
  contexts: FormContext[]
  activeContext: string
  onSelect: (key: string) => void
  countFor: (key: string) => number
  groupLabel: string
}

/** A context selector, not a configuration matrix — clicking a chip only
 * changes which context's requirements are being edited below.
 *
 * Deliberately not antd `Steps`: that component reads as progress, so every
 * context before the active one drew a completion tick nobody had earned,
 * and its fixed-width titles clipped "Create Lead" and "Exploration" rather
 * than wrapping. A rail of chips has no width to run out of, and the count
 * on each chip says what a tick was pretending to.
 *
 * These are pressed-state toggles in a group, not ARIA tabs. A tablist owes
 * its user roving tabindex and arrow-key movement; these are ordinary buttons
 * that Tab steps through one by one, so claiming the tab role would describe
 * a keyboard contract this does not honour. */
export function ContextStepper({
  contexts,
  activeContext,
  onSelect,
  countFor,
  groupLabel
}: ContextStepperProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div
        aria-label={groupLabel}
        className="flex flex-wrap gap-2"
        role="group"
      >
        {contexts.map((context) => {
          const active = context.key === activeContext
          const count = countFor(context.key)
          return (
            <button
              aria-pressed={active}
              className={[
                "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors",
                active
                  ? "border-primary bg-primary text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:border-primary hover:text-primary"
              ].join(" ")}
              key={context.key}
              onClick={() => onSelect(context.key)}
              type="button"
            >
              <span className="whitespace-nowrap">{context.label}</span>
              <span
                aria-label={`${count} field${count === 1 ? "" : "s"} configured`}
                className={[
                  "min-w-5 rounded-full px-1.5 text-xs leading-5",
                  active
                    ? "bg-white/25 text-white"
                    : count > 0
                      ? "bg-gray-100 text-gray-600"
                      : "bg-transparent text-gray-300"
                ].join(" ")}
              >
                {count}
              </span>
            </button>
          )
        })}
      </div>
      <Text className="text-xs" type="secondary">
        Each context inherits everything the ones before it require; the count
        is what that context adds of its own.
      </Text>
    </div>
  )
}
